import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  type OnChanges,
  type OnDestroy,
  type OnInit,
} from '@angular/core';
import { UntypedFormBuilder, Validators, type UntypedFormArray, type UntypedFormGroup } from '@angular/forms';

import { Subject, Subscription } from 'rxjs';
import { debounceTime, filter, take } from 'rxjs/operators';

import { LoaderType } from '@fcom/common/';
import { GtmService } from '@fcom/common/gtm';
import type { SelectOption } from '@fcom/common/interfaces';
import { Airline } from '@fcom/common/interfaces/airlines';
import { ScrollService } from '@fcom/common/services/scroll/scroll.service';
import type { Location } from '@fcom/core-api';
import type { Profile } from '@fcom/core-api/login';
import { ConfigService, LocationMatchOnline, LocationRouteCffService } from '@fcom/core/services';
import { EMAIL_PATTERN, getPartnerAirlines, LocalDate, NAME_PATTERN, unsubscribe } from '@fcom/core/utils';
import {
  AUTOCOMPLETE_DEBOUNCE_TIME,
  dateValidator,
  FormStatus,
  frequentFlyerValidator,
  locationFormatValidator,
  softTicketNumberValidator,
  trackFormState,
} from '@fcom/service-forms';
import { DatepickerTheme } from '@fcom/service-forms/components/common/datepicker.component';
import { AutocompleteTheme } from '@fcom/service-forms/components/common/locations-autocomplete.component';
import {
  ATTACHMENT_ALLOWED_FILE_EXTENSIONS,
  ATTACHMENT_MAX_SIZE_IN_BYTES,
  ButtonType,
  ModalButtons,
  TEXTAREA_LIMIT_3000,
  TextInputComponent,
  TextInputType,
} from '@fcom/ui-components';
import { FileUploaderComponent } from '@fcom/ui-components/components/forms/file-uploader/file-uploader.component';
import { LanguageService } from '@fcom/ui-translate';

import {
  LoggedOutMissingPointsFormField,
  type ClaimMissingAviosResponseData,
  type PortalServicesMailerResponseData,
} from '../interfaces';
import { MissingPointsService } from '../services/missing-points.service';
import { bookingClassSelectOptions, FLIGHT_NUMBER_PATTERN, parsePhoneNumber, PHONE_NUMBER_PATTERN } from '../utils';

const CLAIM_MISSING_POINTS = 'claim-missing-points';

@Component({
  selector: 'fin-logged-out-missing-points-form',
  templateUrl: 'logged-out-missing-points-form.component.html',
  styleUrls: ['./logged-out-missing-points-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LoggedOutMissingPointsFormComponent implements OnInit, OnDestroy, OnChanges {
  readonly allowedFileTypes: string[] = ATTACHMENT_ALLOWED_FILE_EXTENSIONS;
  readonly maxAttachments: FileUploaderComponent['maxAttachments'] = 1;
  readonly maxSizeInBytes: FileUploaderComponent['maxSizeInBytes'] = ATTACHMENT_MAX_SIZE_IN_BYTES;
  readonly textareaLimit3000: number = TEXTAREA_LIMIT_3000;

  readonly Airline = Airline;
  readonly AutocompleteTheme = AutocompleteTheme;
  readonly ButtonType = ButtonType;
  readonly DatepickerTheme = DatepickerTheme;
  readonly FormStatus = FormStatus;
  readonly LoaderType = LoaderType;
  readonly LoggedOutMissingPointsFormField = LoggedOutMissingPointsFormField;
  readonly ModalButtons = ModalButtons;
  readonly TextInputType = TextInputType;

  @ViewChild('fileUploader') fileUploader: FileUploaderComponent;

  @ViewChild('formContainer', { static: true })
  formContainer: ElementRef;

  @ViewChild('message') message: TextInputComponent;

  @Input()
  selectedAirlineCode: Airline;

  @Input()
  profile: Profile;

  @Output()
  formComplete: EventEmitter<PortalServicesMailerResponseData> = new EventEmitter();

  destinationLoading$: Subject<boolean> = new Subject();
  departureLoading$: Subject<boolean> = new Subject();
  destinationLocations$: Subject<Location[]> = new Subject();
  departureLocations$: Subject<Location[]> = new Subject();
  reactiveForm: UntypedFormGroup;
  formStatus: FormStatus = FormStatus.INITIAL;
  ticketNumberHelpModalOpen = false;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  // The type matches what Angular expects
  flightNumberFormGroupConfig: { [key: string]: any } = {
    [LoggedOutMissingPointsFormField.FLIGHT_NUMBER]: [
      '',
      [Validators.required, Validators.pattern(FLIGHT_NUMBER_PATTERN)],
    ], // any 2 alphabets, any 1-4 numbers
  };
  companionFormGroupConfig: { [key: string]: any } = {
    [LoggedOutMissingPointsFormField.COMPANION_NAME]: ['', [Validators.required, Validators.pattern(NAME_PATTERN)]],
    [LoggedOutMissingPointsFormField.COMPANION_MEMBER_NUMBER]: ['', [Validators.required, frequentFlyerValidator()]],
  };
  minDate: LocalDate;
  maxDate: LocalDate;
  airlines: SelectOption[];
  bookingClasses = bookingClassSelectOptions;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private fb: UntypedFormBuilder,
    private locationRouteCffService: LocationRouteCffService,
    private languageService: LanguageService,
    private scrollService: ScrollService,
    private gtmService: GtmService,
    private missingPointsService: MissingPointsService,
    private configService: ConfigService
  ) {}

  get characterCount(): number {
    return this.message?.control?.value?.length || 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedAirlineCode && this.reactiveForm) {
      this.reactiveForm.get(LoggedOutMissingPointsFormField.AIRLINE).setValue(changes.selectedAirlineCode.currentValue);
    }
  }

  ngOnInit(): void {
    this.airlines = getPartnerAirlines();

    this.createForm();

    this.destinationLoading$.next(false);
    this.departureLoading$.next(false);

    this.onDestinationChanges();
    this.onDepartureChanges();

    this.watchFieldChanges(this.reactiveForm, this.onAirlineChange.bind(this), LoggedOutMissingPointsFormField.AIRLINE);

    this.minDate = LocalDate.now().minusMonths(6);
    this.maxDate = LocalDate.now().minusDays(14);
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  createForm(): void {
    this.reactiveForm = this.fb.group(
      {
        [LoggedOutMissingPointsFormField.AIRLINE]: [this.selectedAirlineCode, [Validators.required]],
        [LoggedOutMissingPointsFormField.PASSENGER_NAME]: [
          `${this.profile?.firstname || ''} ${this.profile?.lastname || ''}`.trim(),
          [Validators.required, Validators.pattern(NAME_PATTERN)],
        ],
        [LoggedOutMissingPointsFormField.MEMBER_NUMBER]: [
          this.profile?.memberNumber || '',
          [Validators.required, frequentFlyerValidator()],
        ],
        [LoggedOutMissingPointsFormField.TICKET_NUMBER]: ['', [softTicketNumberValidator()]],
        [LoggedOutMissingPointsFormField.BOOKING_CLASS]: [''],
        [LoggedOutMissingPointsFormField.FLIGHT_FROM]: ['', [Validators.required, locationFormatValidator()]],
        [LoggedOutMissingPointsFormField.FLIGHT_TO]: ['', [Validators.required, locationFormatValidator()]],
        [LoggedOutMissingPointsFormField.DEPARTURE_DATE]: ['', [Validators.required, dateValidator()]],
        [LoggedOutMissingPointsFormField.RETURN_DATE]: ['', [dateValidator()]],
        [LoggedOutMissingPointsFormField.FLIGHT_NUMBERS]: this.fb.array([]),
        [LoggedOutMissingPointsFormField.COMPANIONS]: this.fb.array([]),
        [LoggedOutMissingPointsFormField.MESSAGE]: ['', [Validators.maxLength(this.textareaLimit3000)]],
        [LoggedOutMissingPointsFormField.EMAIL]: [
          this.profile?.email || '',
          [Validators.required, Validators.pattern(EMAIL_PATTERN)],
        ],
        [LoggedOutMissingPointsFormField.PHONE]: [
          parsePhoneNumber(this.profile),
          [Validators.required, Validators.pattern(PHONE_NUMBER_PATTERN)],
        ],
        [LoggedOutMissingPointsFormField.ATTACHMENT]: [[]],
      },
      {
        validator: this.memberNumberDuplicate(),
      }
    );
  }

  onSubmit(): void {
    if (this.formStatus === FormStatus.LOADING) {
      return;
    }

    if (!this.reactiveForm.valid) {
      this.reactiveForm.markAllAsTouched();
      this.reactiveForm.updateValueAndValidity();

      return;
    }

    this.formStatus = FormStatus.LOADING;
    this.scrollService.scrollToImmediate(this.formContainer);
    trackFormState(CLAIM_MISSING_POINTS, 'send-attempt', this.gtmService);

    if (this.configService.cfg.enableNewClaimMissingPointFlow) {
      this.subscriptions.add(
        this.missingPointsService
          .claimFlightsMissingAvios(this.reactiveForm.value)
          .pipe(take(1))
          .subscribe({
            next: (response): void => {
              this.onSuccess(response);
            },
            error: (response: HttpErrorResponse): void => {
              this.onFormSubmitFailed(response);
            },
          })
      );
    } else {
      this.subscriptions.add(
        this.missingPointsService
          .loggedOutMissingPointsRequest(this.reactiveForm.value)
          .pipe(take(1))
          .subscribe({
            next: (response): void => {
              this.onSuccess(response as ClaimMissingAviosResponseData);
            },
            error: (error: unknown): void => {
              this.onFormSubmitFailed(error as HttpErrorResponse);
            },
          })
      );
    }
  }

  private onSuccess(response: ClaimMissingAviosResponseData): void {
    this.formComplete.emit(response);
    trackFormState(CLAIM_MISSING_POINTS, 'success', this.gtmService);
    this.formStatus = FormStatus.COMPLETE;
  }

  private onFormSubmitFailed(response: HttpErrorResponse) {
    this.formComplete.emit(response.error);
    this.formStatus = FormStatus.ERROR;
    trackFormState(CLAIM_MISSING_POINTS, 'error', this.gtmService);
  }

  showHelp(event: Event): void {
    event.preventDefault();
    this.ticketNumberHelpModalOpen = true;
  }

  private queryLocations(queryString: string, locations$: Subject<Location[]>, loading$: Subject<boolean>) {
    const locale = this.languageService.localeValue;

    loading$.next(true);

    this.subscriptions.add(
      this.locationRouteCffService
        .locationMatchesFor(queryString, locale, LocationMatchOnline.ALL)
        .subscribe((matchedLocations): void => {
          loading$.next(false);
          locations$.next(matchedLocations.items);
        })
    );
  }

  private onAirlineChange(airline: string): void {
    const inputField = this.reactiveForm.get(LoggedOutMissingPointsFormField.BOOKING_CLASS);

    if (airline === Airline.FINNAIR) {
      inputField.setValidators(null);
      inputField.updateValueAndValidity();
    } else {
      inputField.setValidators(Validators.required);
      inputField.updateValueAndValidity();
    }
  }

  private onDepartureChanges(): void {
    this.subscriptions.add(
      this.reactiveForm
        .get(LoggedOutMissingPointsFormField.FLIGHT_FROM)
        .valueChanges.pipe(
          debounceTime(AUTOCOMPLETE_DEBOUNCE_TIME),
          filter((val): boolean => {
            const formFieldStatus = this.reactiveForm.get(LoggedOutMissingPointsFormField.FLIGHT_FROM).status;
            return !!val && formFieldStatus === 'INVALID';
          })
        )
        .subscribe((val): void => {
          this.queryLocations(val, this.departureLocations$, this.departureLoading$);
        })
    );
  }

  private onDestinationChanges(): void {
    this.subscriptions.add(
      this.reactiveForm
        .get(LoggedOutMissingPointsFormField.FLIGHT_TO)
        .valueChanges.pipe(
          debounceTime(AUTOCOMPLETE_DEBOUNCE_TIME),
          filter((val): boolean => {
            const formFieldStatus = this.reactiveForm.get(LoggedOutMissingPointsFormField.FLIGHT_TO).status;
            return !!val && formFieldStatus === 'INVALID';
          })
        )
        .subscribe((val): void => {
          this.queryLocations(val, this.destinationLocations$, this.destinationLoading$);
        })
    );
  }

  private memberNumberDuplicate() {
    return (formGroup: UntypedFormGroup): void => {
      const { value: member } = formGroup.get([LoggedOutMissingPointsFormField.MEMBER_NUMBER]);
      const { value: companionMember } = formGroup.get([
        LoggedOutMissingPointsFormField.COMPANIONS,
      ]) as UntypedFormArray;
      const companionControl = formGroup.get('companions') as UntypedFormArray;

      if (Array.isArray(companionMember) && companionMember.length > 0) {
        companionMember.forEach((input, index): void => {
          if (member) {
            const control = companionControl.at(index).get('companionMemberNumber');
            const isDuplicate = input.companionMemberNumber === member;
            if (control?.errors?.duplicate && !isDuplicate) {
              control.setErrors(null);
            } else if (!control?.errors && isDuplicate) {
              control.setErrors({ duplicate: true });
            }
          }
        });
      }
    };
  }

  private watchFieldChanges(form: UntypedFormGroup, callback: (value: unknown) => void, field: string): void {
    this.subscriptions.add(
      form.get(field).valueChanges.subscribe((val): void => {
        callback(val);
      })
    );
  }
}
